import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { getOrientationAssistantResponse } from '../../services/openaiService';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  const contextualGreetings = {
    '/homepage': 'Bonjour ! Je suis votre assistant IA d\'orientation. Je peux vous aider à explorer vos options d\'éducation au Maroc. Par où souhaitez-vous commencer ?',
    '/lycee-orientation': 'Je vois que vous explorez les filières du lycée. En tant qu\'assistant IA, je peux vous donner des conseils personnalisés sur les différents baccalauréats. Que souhaitez-vous savoir ?',
    '/post-bac-database': 'Vous recherchez des établissements post-bac ? Mon intelligence artificielle peut vous aider à affiner votre recherche selon vos critères spécifiques.',
    '/institution-details': 'Vous consultez les détails d\'un établissement. Je peux répondre à toutes vos questions sur les programmes, admissions, et perspectives de carrière.',
    '/admission-calendar': 'Je peux vous aider avec les échéances d\'admission et créer un planning personnalisé. Souhaitez-vous des conseils sur votre stratégie de candidature ?',
    '/student-profile': 'Votre profil est essentiel pour des recommandations IA personnalisées. Je peux vous guider pour l\'optimiser au maximum.'
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      const greeting = contextualGreetings?.[location?.pathname] || contextualGreetings?.['/homepage'];
      setMessages([{
        id: 1,
        text: greeting,
        sender: 'bot',
        timestamp: new Date()
      }]);
      setError(null);
    }
  }, [isOpen, location?.pathname]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputMessage?.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setError(null);

    try {
      // Get AI response from OpenAI
      const aiResponse = await getOrientationAssistantResponse(
        inputMessage, 
        location?.pathname
      );

      const botResponse = {
        id: Date.now() + 1,
        text: aiResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Show error message to user
      const errorResponse = {
        id: Date.now() + 1,
        text: 'Désolé, je rencontre des difficultés techniques. Pouvez-vous réessayer dans un moment ? En attendant, n\'hésitez pas à explorer les différentes sections de l\'application.',
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorResponse]);
      setError('Erreur de connexion au service IA');
    } finally {
      setIsTyping(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setError(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={toggleChat}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-interactive hover:bg-primary/90 transition-smooth flex items-center justify-center ${
          isOpen ? 'scale-95' : 'hover:scale-105'
        }`}
        aria-label="Assistant IA d'orientation"
      >
        <Icon name={isOpen ? 'X' : 'MessageCircle'} size={24} />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-96 h-96 bg-card border border-border rounded-lg shadow-interactive flex flex-col animate-slide-down">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Icon name="Bot" size={16} color="white" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Assistant IA OrientationMaroc</h3>
                <p className="text-xs text-success flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Alimenté par Google Gemini
                </p>
              </div>
            </div>
            <button
              onClick={toggleChat}
              className="p-1 hover:bg-muted rounded transition-smooth"
              aria-label="Fermer le chat"
            >
              <Icon name="Minimize2" size={16} />
            </button>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20">
              <p className="text-xs text-destructive flex items-center gap-2">
                <Icon name="AlertTriangle" size={12} />
                {error}
              </p>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages?.map((message) => (
              <div
                key={message?.id}
                className={`flex ${message?.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                    message?.sender === 'user' ?'bg-primary text-primary-foreground'
                      : message?.isError
                      ? 'bg-destructive/10 text-destructive border border-destructive/20' :'bg-muted text-foreground'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{message?.text}</div>
                  {message?.sender === 'bot' && !message?.isError && (
                    <div className="text-xs opacity-60 mt-1 flex items-center gap-1">
                      <Icon name="Zap" size={10} />
                      IA
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-muted text-foreground px-3 py-2 rounded-lg text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs text-muted-foreground">IA réfléchit...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder={isTyping ? "L'IA répond..." : "Posez votre question..."}
                disabled={isTyping}
                className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={!inputMessage?.trim() || isTyping}
                className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth flex items-center gap-1"
              >
                {isTyping ? (
                  <Icon name="Loader2" size={16} className="animate-spin" />
                ) : (
                  <Icon name="Send" size={16} />
                )}
              </button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">
                Appuyez sur Entrée pour envoyer
              </p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="Sparkles" size={10} />
                Gemini Pro
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Mobile Responsive Adjustments */}
      <style jsx>{`
        @media (max-width: 768px) {
          .fixed.bottom-24.right-6 {
            bottom: 6rem;
            right: 1rem;
            left: 1rem;
            width: auto;
            max-width: calc(100vw - 2rem);
          }
          
          .fixed.bottom-6.right-6 {
            bottom: 1.5rem;
            right: 50%;
            transform: translateX(50%);
          }
        }
      `}</style>
    </>
  );
};

export default ChatbotWidget;